import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime, formatINR } from "../../lib/format";
import { periodLabel } from "../../lib/regularisation";

const ST_CLS = { pending_approval: "bg-amber-100 text-amber-800", approved: "bg-sky-100 text-sky-800", applied: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700", superseded: "bg-slate-100 text-slate-500" };

function DecideDialog({ adj, onClose, onDone }) {
    const [decision, setDecision] = useState("approved");
    const [reason, setReason] = useState("");
    if (!adj) return null;
    const go = async () => {
        try {
            await api.post(`/attendance/payroll-adjustments/${adj.id}/decide`, { decision, reason });
            toast.success(`Adjustment ${decision}.`);
            onDone();
        } catch (e) { toast.error(formatApiError(e, "Decision failed")); }
    };
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="adj-decide-dialog">
                <DialogHeader><DialogTitle>Decide {adj.id}</DialogTitle><DialogDescription>{adj.calculation}</DialogDescription></DialogHeader>
                <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={decision} onChange={(e) => setDecision(e.target.value)} data-testid="adj-decision">
                    <option value="approved">Approve</option><option value="rejected">Reject</option>
                </select>
                <div><Label className="text-xs">Reason (required)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adj-decision-reason" /></div>
                <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={reason.trim().length < 10} data-testid="adj-decision-confirm">Save</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ApplyDialog({ adj, onClose, onDone }) {
    const [runs, setRuns] = useState([]);
    const [runId, setRunId] = useState("");
    useEffect(() => {
        if (adj) api.get(`/attendance/payroll-adjustments/${adj.id}/eligible-runs`).then((r) => { setRuns(r.data.items || []); setRunId((r.data.items || []).find((x) => x.has_employee_line)?.id || ""); }).catch((e) => toast.error(formatApiError(e, "Load failed")));
    }, [adj]);
    if (!adj) return null;
    const go = async () => {
        try {
            await api.post(`/attendance/payroll-adjustments/${adj.id}/apply`, { run_id: runId });
            toast.success("Applied. Recalculate the payroll run to include it.");
            onDone();
        } catch (e) { toast.error(formatApiError(e, "Apply failed")); }
    };
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="adj-apply-dialog">
                <DialogHeader><DialogTitle>Apply to the next open payroll</DialogTitle><DialogDescription>{adj.direction === "arrears" ? "Adds a one-time arrears earning" : "Adds a one-time recovery deduction"} of {formatINR(Math.abs(adj.amount || 0))}. The original paid payslip is not changed.</DialogDescription></DialogHeader>
                {runs.length === 0 ? <div className="text-xs text-amber-800" data-testid="adj-no-runs">No open (Draft / Calculated) payroll run after {adj.original_period} yet. Create and calculate the next run first.</div> : (
                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={runId} onChange={(e) => setRunId(e.target.value)} data-testid="adj-run-select">
                        {runs.map((r) => <option key={r.id} value={r.id} disabled={!r.has_employee_line}>{r.id} · {r.period_start} → {r.period_end} · {r.status}{r.has_employee_line ? "" : " (employee not calculated yet)"}</option>)}
                    </select>
                )}
                <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={go} disabled={!runId} data-testid="adj-apply-confirm">Apply</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function AttendancePayrollAdjustments() {
    const { user, can } = useAuth();
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [decide, setDecide] = useState(null);
    const [apply, setApply] = useState(null);
    const load = useCallback(async () => {
        try { setRows((await api.get("/attendance/payroll-adjustments", { params: { status_filter: filter } })).data.items || []); }
        catch (e) { toast.error(formatApiError(e, "Could not load adjustments")); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);
    const canApprove = user?.role === "super_admin" || can("payroll:approve");
    const canApply = can("payroll:admin");

    return (
        <div className="max-w-[1400px]" data-testid="attendance-adjustments-page">
            <PageHeader eyebrow="Payroll" title="Attendance arrears & recoveries" description="Created when a month that was already paid is re-finalised with different paid days. A second payroll authoriser approves, then payroll applies it to the next open run." />
            <div className="px-6 sm:px-8 py-5 space-y-3">
                <div className="flex gap-2" data-testid="adj-filters">
                    {[["", "All"], ["pending_approval", "Awaiting approval"], ["approved", "Approved — to apply"], ["applied", "Applied"], ["rejected", "Rejected"]].map(([k, l]) => (
                        <button key={k || "all"} onClick={() => setFilter(k)} className={`rounded-full border px-3 py-1 text-xs ${filter === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200"}`} data-testid={`adj-filter-${k || "all"}`}>{l}</button>
                    ))}
                </div>
                <section className="bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-600"><tr>{["Status", "Employee", "Month", "Paid days", "Change", "Amount", "Created", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
                        <tbody>
                            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">No adjustments.</td></tr>}
                            {rows.map((a) => (
                                <tr key={a.id} className="border-t border-slate-100 align-top" data-testid={`adj-row-${a.id}`}>
                                    <td className="px-3 py-2"><Badge className={`text-[10px] ${ST_CLS[a.status] || ""}`}>{a.status.replace("_", " ")}</Badge></td>
                                    <td className="px-3 py-2 text-xs">{a.employee_name}<div className="text-[10px] text-slate-400 font-mono">{a.employee_code}</div></td>
                                    <td className="px-3 py-2 text-xs">{periodLabel(a.original_period)}</td>
                                    <td className="px-3 py-2 text-xs">{a.previous_paid_days} → {a.new_paid_days}</td>
                                    <td className={`px-3 py-2 text-xs font-semibold ${a.delta_paid_days > 0 ? "text-emerald-700" : "text-rose-700"}`}>{a.direction} {a.delta_paid_days > 0 ? "+" : ""}{a.delta_paid_days} d</td>
                                    <td className="px-3 py-2 text-xs tabular-nums">{a.amount != null ? formatINR(a.amount) : "—"}<div className="text-[10px] text-slate-500 max-w-[320px]">{a.calculation}</div></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(a.created_at)}<div>{a.created_by}</div></td>
                                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                                        {a.status === "pending_approval" && canApprove && a.created_by_user_id !== user?.id && <Button size="sm" variant="outline" onClick={() => setDecide(a)} data-testid={`adj-decide-${a.id}`}>Decide</Button>}
                                        {a.status === "approved" && canApply && <Button size="sm" onClick={() => setApply(a)} data-testid={`adj-apply-${a.id}`}>Apply</Button>}
                                        {a.applied_run_id && <span className="text-[10px] text-slate-500">→ {a.applied_run_id}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            </div>
            <DecideDialog adj={decide} onClose={() => setDecide(null)} onDone={() => { setDecide(null); load(); }} />
            <ApplyDialog adj={apply} onClose={() => setApply(null)} onDone={() => { setApply(null); load(); }} />
        </div>
    );
}
